// StreamingProse — the agent's answer with a typewriter reveal while it streams.
//
// docs "everything must FEEL fast": genuine waits should stream, but streamed
// `text_delta` events arrive in bursty network clumps. Appending each delta to
// the message and re-rendering makes the visible cadence track arrival jitter
// ("hackigt"), not a smooth flow. This component DECOUPLES the visual reveal
// from delta arrival: the full streamed-so-far markdown stays in `markdown`
// (the turnStream fold, keyed by messageId+seq, is unchanged and owns that), and
// a VIEW-LOCAL cursor reveals it at a steady cadence, catching up if the buffer
// runs far ahead so it never lags badly, then settling on the full text.
//
// Pure core / impure shell: the cadence + reveal-granularity math is a pure
// module (typewriter.ts, unit-tested); THIS component is the one impure shell —
// it owns the requestAnimationFrame loop and the prefers-reduced-motion
// matchMedia read, both inside useEffect with proper cleanup (the sanctioned
// impure-at-the-mount pattern, not a buried void call). The reveal is VIEW-LOCAL
// state only, exactly like ChatMessage's ClampedProse expander — never app or
// event state.
//
// Why it stays cheap (the whole point — reparsing markdown every frame IS the
// jank): the cursor advances every frame in a ref, but the revealed STRING only
// changes when the cursor crosses a whole-word (whitespace) boundary
// (`wordBoundaryAtOrBefore`). Prose is re-parsed via a `useMemo` keyed on that
// revealed slice, so it reparses once per word (~a dozen times a second at most)
// — never once per animation frame.
//
// Accessibility (same posture as ClampedProse's line-clamp — reveal is visual
// ONLY): the held-back tail must never be hidden from assistive tech, so the
// FULL markdown is always present in the DOM in a VisuallyHidden layer while the
// animated (partial) layer is aria-hidden. prefers-reduced-motion, and any
// non-streaming (durable / reloaded) message, skip the animation entirely and
// render the full markdown at once — no retro-typewriter on settled text.

import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Prose, type RenderProseLink } from "../../Prose/Prose.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import {
    advanceCursor,
    defaultRevealParams,
    type RevealParams,
    wordBoundaryAtOrBefore,
} from "./typewriter.js";
import "./StreamingProse.css";

export type StreamingProseProps = {
    // The agent's streamed-so-far markdown. Grows as deltas fold in; the reveal
    // cursor advances over it independently of how bursty that growth is.
    readonly markdown: string;
    // Whether this bubble is still being assembled live. The caller derives it
    // from AgentEntry.streamSeq !== undefined. When false the full text renders
    // at once (a durable / reloaded message never retro-animates).
    readonly streaming: boolean;
    // Cadence override (mostly for stories/tests); defaults to a brisk,
    // legible rate with a bounded catch-up lag.
    readonly reveal?: RevealParams;
    // How in-app links render (chat: resource routes → chips); see Prose.
    readonly renderLink?: RenderProseLink;
};

// prefers-reduced-motion, read once on mount and kept live via the standard
// matchMedia change listener (the OS setting can toggle mid-session). Same
// idiom as SyncedTranscriptRenderer's usePrefersReducedMotion — impure, but
// isolated to this hook and cleaned up.
const usePrefersReducedMotion = (): boolean => {
    const [reduced, setReduced] = useState(
        () =>
            typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    );
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return;
        const query = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setReduced(query.matches);
        query.addEventListener("change", onChange);
        return () => query.removeEventListener("change", onChange);
    }, []);
    return reduced;
};

export const StreamingProse = ({
    markdown,
    streaming,
    reveal = defaultRevealParams,
    renderLink,
}: StreamingProseProps): ReactNode => {
    const reducedMotion = usePrefersReducedMotion();
    // Animate only a LIVE bubble, and only when motion is welcome. A durable
    // message or a reduced-motion reader gets the full text immediately.
    const animate = streaming && !reducedMotion;

    // The revealed slice — the ONLY state that drives a reparse. Seeded to the
    // full text when not animating so the very first paint is complete.
    const [revealed, setRevealed] = useState(() => (animate ? "" : markdown));
    // The cursor is a fractional character position; it lives in a ref so it
    // advances every frame WITHOUT forcing a render (only the word-boundary
    // slice does that). It PERSISTS across effect re-runs (each delta restarts
    // the loop) so the reveal never rewinds.
    const cursorRef = useRef(0);
    const lastTsRef = useRef<number | null>(null);
    const { charsPerSecond, maxLagChars } = reveal;

    useEffect(() => {
        if (!animate) {
            // Settle instantly: reduced-motion or a durable/reloaded message.
            cursorRef.current = markdown.length;
            setRevealed(markdown);
            return;
        }
        let frame = 0;
        const tick = (ts: number) => {
            const last = lastTsRef.current ?? ts;
            lastTsRef.current = ts;
            const elapsed = ts - last;
            const next = advanceCursor(
                cursorRef.current,
                markdown.length,
                elapsed,
                {
                    charsPerSecond,
                    maxLagChars,
                },
            );
            cursorRef.current = next;
            const slice = markdown.slice(
                0,
                wordBoundaryAtOrBefore(markdown, next),
            );
            setRevealed((prev) => (prev === slice ? prev : slice));
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(frame);
            lastTsRef.current = null;
        };
    }, [animate, markdown, charsPerSecond, maxLagChars]);

    // Parse the revealed slice only when it CHANGES (word cadence), and the
    // full text only when IT changes (network cadence) — neither per frame.
    const revealedNode = useMemo(
        () => <Prose renderLink={renderLink}>{revealed}</Prose>,
        [revealed, renderLink],
    );
    const fullNode = useMemo(
        () => <Prose renderLink={renderLink}>{markdown}</Prose>,
        [markdown, renderLink],
    );

    if (!animate) {
        return <div className="bilda-streaming-prose">{fullNode}</div>;
    }

    return (
        <div className="bilda-streaming-prose">
            {/* The animated reveal — visual only, hidden from assistive tech. */}
            <div className="bilda-streaming-prose__reveal" aria-hidden="true">
                {revealedNode}
            </div>
            {/* The complete text stays in the DOM for assistive tech (the
                held-back tail is never gated behind the animation). */}
            <VisuallyHidden as="div">{fullNode}</VisuallyHidden>
        </div>
    );
};
